import { useState } from 'react';
import { ChevronDown, HelpCircle, MessageSquareText } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function ContactFaqSection() {
  const { t } = useI18n();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      id: 'faq-0',
      question: t('contactPage.faq.items.0.question', 'What should I include in my message?'),
      answer: t(
        'contactPage.faq.items.0.answer',
        'Share the machine or spare-part name, model, quantity, location, and references so we can help faster.'
      ),
    },
    {
      id: 'faq-1',
      question: t('contactPage.faq.items.1.question', 'Can I ask about both machines and spare parts?'),
      answer: t('contactPage.faq.items.1.answer', 'Yes. Send everything in one request and we will route it correctly.'),
    },
    {
      id: 'faq-2',
      question: t('contactPage.faq.items.2.question', 'How quickly do you reply?'),
      answer: t('contactPage.faq.items.2.answer', 'We aim to reply the same business day whenever possible.'),
    },
    {
      id: 'faq-3',
      question: t('contactPage.faq.items.3.question', 'Can I visit a showroom before ordering?'),
      answer: t(
        'contactPage.faq.items.3.answer',
        'Absolutely. You can visit the nearest showroom and get guidance before deciding.'
      ),
    },
  ];

  const toggleItem = (index) => {
    setOpenIndex((current) => (current === index ? -1 : index));
  };

  return (
    <section className="relative overflow-hidden bg-[var(--dash-blacker)] py-20 sm:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,93,16,0.14)_0%,transparent_30%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.05)_0%,transparent_28%),radial-gradient(circle_at_50%_85%,rgba(255,93,16,0.08)_0%,transparent_35%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[url('/bg/5.webp')] bg-cover bg-center opacity-[0.05]" />

      <div className="relative z-10 container mx-auto px-6 lg:px-10">
        <motion.div
          className="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-12 lg:items-start"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div className="max-w-2xl space-y-6 lg:space-y-7" variants={itemVariants}>
            <AnimatedEyebrow>{t('contactPage.faq.eyebrow')}</AnimatedEyebrow>
            <motion.h2 variants={itemVariants} className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              {t('contactPage.faq.titleLine1')}{' '}
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {t('contactPage.faq.titleLine2')}
              </span>
            </motion.h2>
            <motion.p variants={itemVariants} className="max-w-xl text-sm leading-7 text-slate-400 sm:text-base">
              {t('contactPage.faq.description')}
            </motion.p>

            <motion.div variants={itemVariants} className="mt-8 space-y-4 sm:space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--dash-main)] text-[var(--dash-black)]">
                  <HelpCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.14em] text-white">
                    {t('contactPage.faq.tipTitle')}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {t('contactPage.faq.tipDescription')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--dash-main)] text-[var(--dash-black)]">
                  <MessageSquareText className="h-6 w-6" />
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {t('contactPage.faq.tipNote')}
                </p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="space-y-3 lg:space-y-3 lg:pl-0" variants={containerVariants}>
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;

              return (
                <motion.article
                  key={item.id}
                  variants={itemVariants}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-[var(--dash-black)] transition-all duration-300 hover:border-white/20"
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(index)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left sm:px-8"
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-center gap-4 flex-1">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-[var(--dash-main)]/10 text-sm font-bold text-[var(--dash-main)]">
                        {index + 1}
                      </span>
                      <span className="text-base font-semibold leading-snug text-white sm:text-lg">
                        {item.question}
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-[var(--dash-main)] transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen ? (
                      <motion.div
                        key="faq-answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-white/10 px-6 pb-6 pt-4 sm:px-8 sm:pb-7">
                          <p className="text-sm leading-7 text-slate-400 sm:text-base">
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </motion.article>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
